// ============================================================================
//  AttendanceEnrichedPage.tsx — Pointage Enrichi (M9)
//  3 onglets : Sites de pointage, Photos pointage, Scanner QR
// ============================================================================
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MapPin, Camera, QrCode, Plus, Search, Check, X, Eye, EyeOff,
  RefreshCw, Trash2, Shield, Clock, Users, AlertTriangle,
} from 'lucide-react'
import {
  attendanceEnrichedApi, type AttendanceSite, type AttendancePhoto,
} from '@/modules/attendance-enriched/api/attendance-enriched.api'
import LeafletMap, { type MapMarker as LMapMarker, type MapCircle as LMapCircle } from '@/components/map/LeafletMap'
import { usePermission } from '@/modules/auth/hooks/usePermission'
import { useNotificationStore } from '@/stores/notification.store'
import { KpiCard } from '@/components/ui/KpiCard'
import { Button, Input, Select, Modal, Badge } from '@/components/ui/index'

const toNum = (d: any): number => {
  if (d == null) return 0
  if (typeof d === 'number') return d
  // Prisma Decimal object with toNumber()
  if (typeof d === 'object' && typeof d.toNumber === 'function') return d.toNumber()
  // String from Prisma Decimal.toJSON()
  const n = Number(d)
  return Number.isFinite(n) ? n : 0
}
const fmt = (d?: string | null) => d ? new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

function Empty({ icon: Icon, text }: { icon: any; text: string }) {
  return <div className="flex flex-col items-center justify-center py-20 nx-fade-in"><Icon size={48} className="text-[var(--tx3)] mb-3 opacity-40" /><p className="text-[var(--tx2)]">{text}</p></div>
}

export default function AttendanceEnrichedPage() {
  const [tab, setTab] = useState('sites')
  const tabs = [
    { key: 'sites', label: 'Sites de pointage', icon: MapPin },
    { key: 'photos', label: 'Photos', icon: Camera },
    { key: 'scanner', label: 'Scanner QR', icon: QrCode },
  ]
  return (
    <div className="space-y-5 nx-fade-up">
      <div>
        <h1 className="text-xl font-bold tracking-[-0.02em] text-[var(--tx)]">Pointage Enrichi</h1>
        <p className="text-sm text-[var(--tx3)] mt-1">Sites QR/NFC, photos selfie géolocalisées, pointage mobile</p>
      </div>
      <StatsBar />
      <div className="flex items-center gap-1 p-1 rounded-lg bg-[var(--surface-2)]">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap transition-all ${tab === t.key ? 'bg-white text-[var(--tx)] shadow-sm border border-[var(--bd)]' : 'text-[var(--tx3)] hover:text-[var(--tx)]'}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>
      {tab === 'sites' && <SitesTab />}
      {tab === 'photos' && <PhotosTab />}
      {tab === 'scanner' && <ScannerTab />}
    </div>
  )
}

function StatsBar() {
  const { data: s } = useQuery({ queryKey: ['att-enriched-stats'], queryFn: () => attendanceEnrichedApi.stats() })
  if (!s) return null
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 nx-stagger">
      <KpiCard label="Sites actifs" value={s.activeSites} icon={MapPin} color="var(--brand-orange)" />
      <KpiCard label="Sites total" value={s.totalSites} icon={Shield} color="var(--brand-amber)" />
      <KpiCard label="Photos aujourd'hui" value={s.todayPhotos} icon={Camera} color="#10b981" />
      <KpiCard label="Non validées" value={s.unvalidatedPhotos} icon={AlertTriangle} color="#f59e0b" />
      <KpiCard label="Pointages ce jour" value={s.todayAttendances} icon={Clock} color="var(--brand-amber)" />
    </div>
  )
}

// ━━━ SITES ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function SitesTab() {
  const qc = useQueryClient(); const notify = useNotificationStore(); const { can } = usePermission()
  const [search, setSearch] = useState(''); const [showCreate, setShowCreate] = useState(false)

  const { data: listData, isLoading } = useQuery({ queryKey: ['att-sites', search], queryFn: () => attendanceEnrichedApi.sites({ search: search || undefined, limit: 100 }) })
  const createM = useMutation({ mutationFn: (d: any) => attendanceEnrichedApi.createSite(d), onSuccess: () => { qc.invalidateQueries({ queryKey: ['att-sites'] }); qc.invalidateQueries({ queryKey: ['att-enriched-stats'] }); setShowCreate(false); notify.success('Site créé') } })
  const toggleM = useMutation({ mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => attendanceEnrichedApi.updateSite(id, { isActive }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['att-sites'] }); qc.invalidateQueries({ queryKey: ['att-enriched-stats'] }) } })
  const regenM = useMutation({ mutationFn: (id: string) => attendanceEnrichedApi.regenerateQr(id), onSuccess: () => { qc.invalidateQueries({ queryKey: ['att-sites'] }); notify.success('QR code régénéré') } })
  const deleteM = useMutation({ mutationFn: (id: string) => attendanceEnrichedApi.deleteSite(id), onSuccess: () => { qc.invalidateQueries({ queryKey: ['att-sites'] }); qc.invalidateQueries({ queryKey: ['att-enriched-stats'] }); notify.success('Site supprimé') } })
  const sites = listData?.data ?? []

  // Préparer les cercles pour la carte
  const siteCircles: LMapCircle[] = sites
    .filter((s: AttendanceSite) => s.isActive && s.latitude && s.longitude)
    .map((s: AttendanceSite) => ({
      id: s.id,
      lat: toNum(s.latitude),
      lng: toNum(s.longitude),
      radius: s.radiusMeters,
      color: 'var(--brand-orange)',
      fillColor: 'var(--brand-orange)',
      label: s.name,
      popup: `<b>${s.name}</b><br/>${s.address || ''}<br/>Rayon: ${s.radiusMeters}m<br/>QR: ${s.qrCode}`,
    }))

  return (
    <>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-xs"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tx3)]" /><Input placeholder="Rechercher un site..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" /></div>
        {can('attendance_enriched:manage') && <Button leftIcon={<Plus size={16} />} onClick={() => setShowCreate(true)}>Nouveau site</Button>}
      </div>

      {/* Carte des sites de pointage */}
      {siteCircles.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-[var(--tx3)]">{siteCircles.length} site(s) actif(s) sur la carte — cercles = zone de pointage autorisée</p>
          <LeafletMap height={350} circles={siteCircles} />
        </div>
      )}
      {isLoading ? <div className="text-center py-20 text-[var(--tx3)]">Chargement…</div> : !sites.length ? <Empty icon={MapPin} text="Aucun site de pointage" /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{sites.map(s => (
          <div key={s.id} className={`bg-white border rounded-xl p-4 transition-all ${s.isActive ? 'border-[var(--bd)] hover:shadow-md' : 'border-dashed border-gray-300 opacity-60'}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-[var(--tx)]">{s.name}</h3>
              {!s.isActive && <Badge variant="outline" size="sm">Inactif</Badge>}
            </div>
            <div className="space-y-1.5 text-xs text-[var(--tx3)]">
              {s.address && <div className="flex items-center gap-1"><MapPin size={10} />{s.address}</div>}
              <div>Coordonnées : {toNum(s.latitude).toFixed(4)}, {toNum(s.longitude).toFixed(4)}</div>
              <div>Rayon : {s.radiusMeters}m</div>
              <div className="flex items-center gap-1.5 mt-2">
                <span className="font-mono text-[11px] bg-[var(--surface-2)] px-2 py-1 rounded flex items-center gap-1"><QrCode size={12} />{s.qrCode}</span>
                {s.nfcTag && <span className="text-[11px] bg-blue-50 text-blue-600 px-2 py-1 rounded">NFC</span>}
              </div>
            </div>
            {can('attendance_enriched:manage') && (
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[var(--bd)]">
                <button onClick={() => toggleM.mutate({ id: s.id, isActive: !s.isActive })} className="p-1.5 rounded-md hover:bg-[var(--surface-2)]" title={s.isActive ? 'Désactiver' : 'Activer'}>{s.isActive ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                <button onClick={() => regenM.mutate(s.id)} className="p-1.5 rounded-md hover:bg-[var(--surface-2)]" title="Régénérer QR"><RefreshCw size={14} /></button>
                <button onClick={() => { if (confirm('Supprimer ce site ?')) deleteM.mutate(s.id) }} className="p-1.5 rounded-md hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
              </div>
            )}
          </div>
        ))}</div>
      )}
      {showCreate && <CreateSiteModal onClose={() => setShowCreate(false)} onSubmit={d => createM.mutate(d)} loading={createM.isPending} />}
    </>
  )
}

// ━━━ PHOTOS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function PhotosTab() {
  const qc = useQueryClient(); const notify = useNotificationStore(); const { can } = usePermission()
  const [validF, setValidF] = useState(''); const [typeF, setTypeF] = useState('')

  const { data: listData, isLoading } = useQuery({ queryKey: ['att-photos', validF, typeF], queryFn: () => attendanceEnrichedApi.photos({ isValidated: validF === '' ? undefined : validF === 'true', type: typeF || undefined, limit: 100 }) })
  const validateM = useMutation({ mutationFn: ({ id, v }: { id: string; v: boolean }) => attendanceEnrichedApi.validatePhoto(id, v), onSuccess: () => { qc.invalidateQueries({ queryKey: ['att-photos'] }); qc.invalidateQueries({ queryKey: ['att-enriched-stats'] }); notify.success('Photo mise à jour') } })
  const photos = listData?.data ?? []

  return (
    <>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <Select value={typeF} onChange={e => setTypeF(e.target.value)}><option value="">Tous types</option><option value="clock_in">Entrée</option><option value="clock_out">Sortie</option></Select>
        <Select value={validF} onChange={e => setValidF(e.target.value)}><option value="">Toutes</option><option value="false">Non validées</option><option value="true">Validées</option></Select>
      </div>
      {isLoading ? <div className="text-center py-20 text-[var(--tx3)]">Chargement…</div> : !photos.length ? <Empty icon={Camera} text="Aucune photo de pointage" /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{photos.map(p => (
          <div key={p.id} className="nx-card overflow-hidden hover:shadow-md transition-all">
            <div className="aspect-[4/3] bg-[var(--surface-2)] relative">
              <img src={p.photoUrl} alt="Selfie pointage" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
              <div className="absolute top-2 left-2 flex gap-1">
                <Badge variant={p.type === 'clock_in' ? 'success' : 'default'} size="sm">{p.type === 'clock_in' ? 'Entrée' : 'Sortie'}</Badge>
                {p.isValidated ? <Badge variant="success" size="sm">✓ Validé</Badge> : <Badge variant="warning" size="sm">En attente</Badge>}
              </div>
            </div>
            <div className="p-3">
              <div className="text-sm font-medium text-[var(--tx)]">
                {p.attendance?.employee?.user ? `${p.attendance.employee.user.firstName} ${p.attendance.employee.user.lastName}` : '—'}
              </div>
              <div className="text-xs text-[var(--tx3)] mt-0.5">{fmt(p.createdAt)}</div>
              {p.latitude && <div className="text-xs text-[var(--tx3)]">{toNum(p.latitude).toFixed(4)}, {toNum(p.longitude).toFixed(4)}</div>}
              {can('attendance_enriched:manage') && !p.isValidated && (
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" leftIcon={<Check size={12} />} onClick={() => validateM.mutate({ id: p.id, v: true })}>Valider</Button>
                  <Button size="sm" variant="ghost" leftIcon={<X size={12} />} onClick={() => validateM.mutate({ id: p.id, v: false })}>Rejeter</Button>
                </div>
              )}
            </div>
          </div>
        ))}</div>
      )}
    </>
  )
}

// ━━━ SCANNER QR ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ScannerTab() {
  const notify = useNotificationStore()
  const [qrCode, setQrCode] = useState('')
  const [result, setResult] = useState<any>(null)
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'granted' | 'denied'>('idle')
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const clockM = useMutation({
    mutationFn: (d: { qrCode: string; latitude: number; longitude: number }) => attendanceEnrichedApi.qrClock(d),
    onSuccess: (data) => { setResult(data); notify.success(`${data.action === 'clock_in' ? 'Entrée' : 'Sortie'} enregistrée — ${data.site.name}`) },
    onError: (err: any) => notify.error(err?.response?.data?.message ?? 'Erreur de pointage'),
  })

  const requestLocation = () => {
    if (!navigator.geolocation) {
      notify.error('Géolocalisation non supportée par ce navigateur')
      setGeoStatus('denied')
      return
    }
    setGeoStatus('loading')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setGeoStatus('granted')
      },
      (err) => {
        notify.error(`Géolocalisation refusée : ${err.message}`)
        setGeoStatus('denied')
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    )
  }

  const handleClock = () => {
    if (!qrCode.trim()) return
    if (!coords) {
      requestLocation()
      return
    }
    clockM.mutate({ qrCode: qrCode.trim(), latitude: coords.lat, longitude: coords.lng })
  }

  return (
    <div className="max-w-md mx-auto space-y-6">
      <div className="nx-card p-6 text-center">
        <QrCode size={48} className="mx-auto text-[var(--blue)] mb-4" />
        <h3 className="text-lg font-semibold text-[var(--tx)] mb-1">Pointage par QR Code</h3>
        <p className="text-sm text-[var(--tx3)] mb-4">Scannez le QR du site ou saisissez le code manuellement</p>
        <div className="space-y-3">
          <Input value={qrCode} onChange={e => setQrCode(e.target.value)} placeholder="SITE-XXXXXXXX" className="text-center font-mono" />

          {/* Géolocalisation status */}
          {geoStatus === 'idle' && (
            <Button variant="outline" onClick={requestLocation} className="w-full">
              <MapPin size={16} className="mr-2" />Activer la géolocalisation
            </Button>
          )}
          {geoStatus === 'loading' && (
            <div className="flex items-center justify-center gap-2 text-sm text-[var(--tx3)] py-2">
              <div className="w-4 h-4 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
              Localisation en cours…
            </div>
          )}
          {geoStatus === 'granted' && coords && (
            <div className="text-xs text-green-600 bg-green-50 rounded-lg p-2">
              <MapPin size={12} className="inline mr-1" />Position : {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
            </div>
          )}
          {geoStatus === 'denied' && (
            <div className="text-xs text-red-600 bg-red-50 rounded-lg p-2">
              <AlertTriangle size={12} className="inline mr-1" />Géolocalisation refusée. Activez la dans les paramètres de votre navigateur.
              <button onClick={requestLocation} className="underline ml-1">Réessayer</button>
            </div>
          )}

          <Button onClick={handleClock} isLoading={clockM.isPending}
            disabled={!qrCode.trim() || geoStatus !== 'granted'} className="w-full">
            <Clock size={16} className="mr-2" />Pointer
          </Button>
        </div>
        <p className="text-xs text-[var(--tx3)] mt-3">Vous devez être à proximité du site pour pointer.</p>
      </div>

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <Check size={24} className="mx-auto text-green-600 mb-2" />
          <h4 className="font-semibold text-green-800">{result.action === 'clock_in' ? 'Entrée enregistrée' : 'Sortie enregistrée'}</h4>
          <p className="text-sm text-green-700 mt-1">Site : {result.site.name}</p>
          <p className="text-xs text-green-600">Distance : {result.distance}m du site</p>
        </div>
      )}

      {clockM.isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
          <AlertTriangle size={24} className="mx-auto text-red-600 mb-2" />
          <p className="text-sm text-red-700">{(clockM.error as any)?.response?.data?.message ?? 'Erreur de pointage'}</p>
        </div>
      )}
    </div>
  )
}

// ━━━ CREATE SITE MODAL ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function CreateSiteModal({ onClose, onSubmit, loading }: { onClose: () => void; onSubmit: (d: any) => void; loading: boolean }) {
  const [f, setF] = useState({ name: '', address: '', latitude: '', longitude: '', radiusMeters: '100', nfcTag: '' })
  const set = (k: string, v: any) => setF(p => ({ ...p, [k]: v }))
  return (
    <Modal open onClose={onClose} title="Nouveau site de pointage" size="md">
      <div className="space-y-4">
        <div><label className="block text-sm font-medium text-[var(--tx)] mb-1">Nom du site *</label><Input value={f.name} onChange={e => set('name', e.target.value)} placeholder="Siège NexusCorp" /></div>
        <div><label className="block text-sm font-medium text-[var(--tx)] mb-1">Adresse</label><Input value={f.address} onChange={e => set('address', e.target.value)} placeholder="Rue X, Dakar" /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-sm font-medium text-[var(--tx)] mb-1">Latitude *</label><Input type="number" step="any" value={f.latitude} onChange={e => set('latitude', e.target.value)} placeholder="14.6937" /></div>
          <div><label className="block text-sm font-medium text-[var(--tx)] mb-1">Longitude *</label><Input type="number" step="any" value={f.longitude} onChange={e => set('longitude', e.target.value)} placeholder="-17.4441" /></div>
          <div><label className="block text-sm font-medium text-[var(--tx)] mb-1">Rayon (m)</label><Input type="number" value={f.radiusMeters} onChange={e => set('radiusMeters', e.target.value)} /></div>
        </div>
        <div><label className="block text-sm font-medium text-[var(--tx)] mb-1">Tag NFC (optionnel)</label><Input value={f.nfcTag} onChange={e => set('nfcTag', e.target.value)} placeholder="NFC-XXXX" /></div>
        <p className="text-xs text-[var(--tx3)]">Le QR code sera généré automatiquement à la création du site.</p>
      </div>
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[var(--bd)]">
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
        <Button onClick={() => onSubmit({
          name: f.name, address: f.address || undefined,
          latitude: parseFloat(f.latitude), longitude: parseFloat(f.longitude),
          radiusMeters: parseInt(f.radiusMeters) || 100,
          nfcTag: f.nfcTag || undefined,
        })} disabled={!f.name || !f.latitude || !f.longitude} isLoading={loading}>Créer</Button>
      </div>
    </Modal>
  )
}
